import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ToggleOptionComponent } from 'app/shared/components/form-ui/toggle-option/toggle-option.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { AttachmentConfigEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/attachment-config.enum';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { EntityEnum } from "@albatrosdeveloper/ave-models-package/lib/enums/entity.enum";
import { AttachmentConfigAttributes } from '@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/attachments-config/attachment-config.entity';

@Component({
  selector: 'app-attachment-config-dialog',
  standalone: true,
  imports: [
    CommonModule, MatDialogModule, ReactiveFormsModule, MatFormFieldModule, 
    MatInputModule, MatButtonModule, MatSelectModule, ToggleOptionComponent, MatIconModule
  ],
  template: `
    <div class="flex flex-col w-[600px] max-w-full overflow-hidden">
      <h2 mat-dialog-title class="!m-0 px-8 py-6 text-xl font-bold border-b text-slate-700 flex justify-between items-center">
        Configuración de Adjuntos
        <button *ngIf="form.get('multiple').value" 
                mat-stroked-button 
                color="primary" 
                (click)="addAttachment()"
                class="rounded-lg">
          <mat-icon>add</mat-icon> Agregar otro
        </button>
      </h2>

      <mat-dialog-content class="!p-8">
        <form [formGroup]="form">
          <div class="mb-8 p-4 bg-blue-50/50 rounded-2xl border border-blue-100">
             <app-toggle-option 
                title="Permitir múltiples tipos de archivos" 
                subtitle="Habilita la carga de diferentes documentos (Ej: Acta, Identificación, etc.)"
                icon="layers" 
                iconColor="blue"
                formControlName="multiple"
                (change)="onMultipleChange($event)">
              </app-toggle-option>
              <p *ngIf="attachments.length > 1 && !form.get('multiple').value" class="text-xs text-red-500 mt-2 px-2">
                * Debe eliminar los registros adicionales antes de deshabilitar esta opción.
              </p>
          </div>

          <div formArrayName="attachments" class="flex flex-col gap-6">
            <div *ngFor="let item of attachments.controls; let i = index" 
                 [formGroupName]="i" 
                 class="relative p-6 bg-slate-50 rounded-2xl border border-slate-200">
              
              <div class="flex justify-between items-center mb-4">
                <span class="text-xs font-bold uppercase text-slate-400">Adjunto #{{i + 1}}</span>
                <button *ngIf="attachments.length > 1" 
                        mat-icon-button color="warn" (click)="removeAttachment(i)">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <mat-form-field appearance="outline" class="col-span-2">
                  <mat-label>Nombre del adjunto</mat-label>
                  <input matInput formControlName="name" placeholder="Ej: Documento de Identidad">
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Extensiones</mat-label>
                  <mat-select formControlName="allowedExtensions" multiple>
                    <mat-option *ngFor="let opt of extensionOptions" [value]="opt.value">{{ opt.label }}</mat-option>
                  </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Peso Máx (MB)</mat-label>
                  <input matInput type="number" formControlName="maxFileSize">
                </mat-form-field>

                <mat-form-field appearance="outline" class="col-span-2" *ngIf="data?.entity === EntityEnumList.CREDIT_APPLICATION">
                  <mat-label>Aplica a</mat-label>
                  <mat-select formControlName="applyTo" multiple>
                    <mat-option *ngFor="let opt of applyOptions" [value]="opt.value">{{ opt.label }}</mat-option>
                  </mat-select>
                </mat-form-field>

                <div class="col-span-2">
                   <app-toggle-option 
                    title="Es obligatorio" 
                    icon="priority_high" 
                    iconColor="orange"
                    formControlName="isRequired">
                  </app-toggle-option>
                </div>
              </div>
            </div>
          </div>
        </form>
      </mat-dialog-content>

      <mat-dialog-actions align="end" class="!m-0 px-8 py-6 border-t bg-gray-50/50">
        <button mat-button (click)="dialogRef.close()" class="rounded-xl px-6 mr-2 text-slate-500">Cancelar</button>
        <button mat-flat-button color="primary" (click)="save()" class="rounded-xl px-8 shadow-sm" 
                [disabled]="form.invalid || (attachments.length > 1 && !form.get('multiple').value)">
          Guardar Configuración
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    :host ::ng-deep .mat-mdc-dialog-content { padding: 2rem !important; max-height: 65vh; }
  `]
})
export class AttachmentConfigDialogComponent implements OnInit {
  form: FormGroup;
  extensionOptions = [
    { label: 'PDF', value: '.pdf' }, { label: 'JPG', value: '.jpg,' },
    { label: 'JPEG', value: '.jpeg' },
    { label: 'PNG', value: '.png' }, { label: 'Word', value: '.doc, .docx' },
    { label: '.xls', value: '.xls' }, { label: '.xlsx', value: '.xlsx' }
  ];

  applyOptions = [
    { label: 'Apertura', value: AttachmentConfigEnum.ISFOROPENING },
    { label: 'Extensión', value: AttachmentConfigEnum.ISFOREXTENSION }
  ];

  public readonly EntityEnumList = EntityEnum;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AttachmentConfigDialogComponent>,
    private handlerMessage: HandlerMessageService,
    @Inject(MAT_DIALOG_DATA) public data: { attachments: AttachmentConfigAttributes[], multiple: boolean, entity?: EntityEnum }
  ) {
    this.form = this.fb.group({
      multiple: [!!this.data.multiple],
      attachments: this.fb.array([])
    });
  }

  ngOnInit() {
    if (this.data.attachments && this.data.attachments.length > 0) {
      this.data.attachments.forEach(att => this.addAttachment(att));
    } else {
      this.addAttachment();
    }
  }

  get attachments() {
    return this.form.get('attachments') as FormArray;
  }

  onMultipleChange(isMultiple: boolean) {
    if (!isMultiple && this.attachments.length > 1) {
      this.handlerMessage.error({
        message: 'Debe de dejar solo 1 registro para que deshabilitar multiple.'
      });
    }
  }

  addAttachment(data: AttachmentConfigAttributes = null) {
    const isMultiple = this.form.get('multiple').value;
    if (!isMultiple && this.attachments.length >= 1) return;

    const group = this.fb.group({
      name: [data?.name || '', Validators.required],
      allowedExtensions: [data?.allowedExtensions ? data.allowedExtensions.split(', ') : []],
      maxFileSize: [data?.maxFileSize || 5],
      applyTo: [data?.applyTo || []],
      isRequired: [!!data?.isRequired]
    });
    this.attachments.push(group);
  }

  removeAttachment(index: number) {
    this.attachments.removeAt(index);
  }

  save() {
    const rawValue = this.form.getRawValue();
    const formattedAttachments = rawValue.attachments.map((item: AttachmentConfigAttributes) => ({
      ...item,
      allowedExtensions: Array.isArray(item.allowedExtensions) ? item.allowedExtensions.join(', ') : item.allowedExtensions
    }));
    
    this.dialogRef.close({ 
      attachments: formattedAttachments,
      multiple: rawValue.multiple 
    });
  }
}